import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import PageHero from '../components/layout/PageHero';
import { FileText, Wrench, Cpu, Users, ChevronRight, BookOpen } from 'lucide-react';

const sections = [
  {
    icon: FileText,
    title: 'Articles',
    to: '/articles',
    description:
      'In-depth technical write-ups on automotive systems and engineering principles, written and reviewed by domain experts. Start here to build foundational knowledge on a topic.',
  },
  {
    icon: Wrench,
    title: 'Tools',
    to: '/tools',
    description:
      "Browser-based calculators for AUTOSAR E2E profiles, CRCs, Data IDs, and byte conversions. Useful once you already know roughly what you're computing and why.",
  },
  {
    icon: Cpu,
    title: 'Simulation',
    to: '/simulation',
    soon: true,
    description:
      'Interactive simulations of vehicle systems and protocols, coming soon — a hands-on way to see how the concepts in Articles actually behave.',
  },
  {
    icon: Users,
    title: 'Community',
    to: '/community',
    description:
      'Ask questions, answer others, and see how experienced engineers reason through real problems. No question is too basic.',
  },
];

const startingPoints = [
  {
    step: '01',
    title: 'Skim the Glossary',
    description: "Get comfortable with the acronyms (CAN, UDS, DTC, E2E) you'll see everywhere else on the site.",
    to: '/glossary',
  },
  {
    step: '02',
    title: 'Read a beginner-friendly Article',
    description: 'Browse Articles and start with anything tagged for fundamentals rather than a niche subsystem.',
    to: '/articles',
  },
  {
    step: '03',
    title: 'Try a Tool with sample data',
    description: 'Open the E2E or CRC calculator and run through a known example to see inputs and outputs line up.',
    to: '/tools',
  },
  {
    step: '04',
    title: 'Ask a question in Community',
    description: "If something didn't click, post it — that's exactly what Community is for.",
    to: '/community',
  },
];

const BeginnersGuidePage = () => {
  return (
    <div className="min-h-screen" data-testid="beginners-guide-page">
      <PageHero
        eyebrow="Getting Started"
        title="Beginner's Guide"
        description="New to automotive engineering, or new to AUTOXIUM? Here's how the site fits together and where to start."
      />

      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight mb-8">
            What Each Section Is For
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {sections.map(({ icon: Icon, title, to, description, soon }) => (
              <Link key={title} to={to} data-testid={`beginners-guide-${title.toLowerCase()}`}>
                <Card className="technical-card h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="h-10 w-10 rounded-md bg-chassis flex items-center justify-center mb-4">
                      <Icon className="h-5 w-5 text-autoxium-blue" />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-chivo text-lg font-bold uppercase tracking-tight">{title}</h3>
                      {soon && (
                        <span className="text-[8px] font-mono bg-autoxium-blue px-1 py-0.5 rounded uppercase text-white leading-none">
                          Soon
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">{description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight mb-8">
            Suggested Starting Points
          </h2>
          <div className="space-y-4 max-w-3xl">
            {startingPoints.map((s) => (
              <Link
                key={s.step}
                to={s.to}
                className="block"
                data-testid={`starting-point-${s.step}`}
              >
                <Card className="technical-card">
                  <CardContent className="p-6 flex items-center gap-6">
                    <span className="font-mono text-2xl font-bold text-autoxium-blue shrink-0">{s.step}</span>
                    <div className="flex-1">
                      <h3 className="font-chivo font-bold uppercase tracking-tight mb-1">{s.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-autoxium-blue shrink-0" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="max-w-3xl mt-12 flex items-start gap-4 rounded-xl border border-structure bg-aluminum p-6">
            <BookOpen className="h-5 w-5 text-autoxium-blue shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              Not sure where a specific concept lives? Check the{' '}
              <Link to="/glossary" className="text-autoxium-blue font-medium">Glossary</Link> first, or{' '}
              <Link to="/request-topic" className="text-autoxium-blue font-medium">request a topic</Link>{' '}
              if you can't find an article on it yet.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BeginnersGuidePage;
